/**
 * Dashboard Plugin System - Registry
 * 
 * Reactive registry store for plugin registrations.
 */

import { reactive, readonly } from 'vue';
import type { PluginRegistry } from './dashboard-types';

function createEmptyRegistry(): PluginRegistry {
  return {
    tabs: {},
    components: {},
    providers: {},
    contentOverrides: {},
    tabVisibility: [],
    hooks: {},
  };
}

// [HMR] In development, preserve the registry across HMR module re-evaluations.
// Without this, any change that causes dashboard-registry.ts to be re-evaluated
// would create a new empty registry, losing all plugin registrations.
// import.meta.hot.data persists between HMR updates of the same module.
const initialState: PluginRegistry =
  (import.meta.hot?.data?.registry as PluginRegistry) ?? createEmptyRegistry();

export const pluginRegistry = reactive<PluginRegistry>(initialState);

if (import.meta.hot) {
  // Save current registry so it's available in the next HMR evaluation
  import.meta.hot.data.registry = pluginRegistry;
  // Self-accept: prevent this module's updates from bubbling to a full page reload
  import.meta.hot.accept();
}

export function getReadonlyRegistry() {
  return readonly(pluginRegistry);
}

export function clearRegistry(): void {
  Object.keys(pluginRegistry.tabs).forEach(key => {
    pluginRegistry.tabs[key] = [];
  });
  Object.keys(pluginRegistry.components).forEach(key => {
    pluginRegistry.components[key] = [];
  });
  Object.keys(pluginRegistry.providers).forEach(key => {
    pluginRegistry.providers[key] = [];
  });
  Object.keys(pluginRegistry.contentOverrides).forEach(key => {
    pluginRegistry.contentOverrides[key] = [];
  });
  pluginRegistry.tabVisibility = [];
  Object.keys(pluginRegistry.hooks).forEach(key => {
    pluginRegistry.hooks[key] = [];
  });
}
